import React from "react";
import * as yup from "yup";
import {useFormik} from "formik";

import lepri from "../../services/lepri-js";
import {useFeedbackTools} from "../../components/FeedbackTools";
import AvatarWithUpload from "../../components/AvatarWithUpload";
import {useGlobalState} from "../../GlobalState";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';


const ThemeDialog = ({onClose, onSave}) => {
    const {logo, updateLogo, theme, updateTheme} = useGlobalState();
    const {showSuccessNotification, showErrorNotification} = useFeedbackTools();

    const handleUpdateLogo = async (file) => {
        const image = await lepri(file, { minSideDimension: 200, mimeType: 'image/png'});
        const formData = new FormData();
        formData.append("logo", image);
        await updateLogo(formData);
    }

    const formik = useFormik({
        initialValues: {
            primaryColor: theme.primaryColor || "#ffffff",
            primaryBackgroundColor: theme.primaryBackgroundColor || "#ffffff",
            secondaryBackgroundColor: theme.secondaryBackgroundColor || "#ffffff",
            titleAlignment: theme.titleAlignment || "left",
        },
        validationSchema: yup.object().shape({
            primaryColor: yup.string().required('Campo obbligatorio'),
            primaryBackgroundColor: yup.string().required('Campo obbligatorio'),
            secondaryBackgroundColor: yup.string().required('Campo obbligatorio'),
            titleAlignment: yup.string().required('Campo obbligatorio'),
        }),
        onSubmit: async (values) => {
            try {
                await updateTheme(values);
                onClose();
                onSave();
                showSuccessNotification("Tema aggiornato");
            } catch (e) {
                showErrorNotification(e);
            }
        }
    });

    return <Dialog open={true}
                   onClose={onClose}
                   aria-labelledby="alert-theme-dialog-title"
                   aria-describedby="alert-dialog-description"
                   fullWidth>

        <DialogTitle id="alert-theme-dialog-title">
            Logo e tema
        </DialogTitle>

        <DialogContent>
            <Stack spacing={3}>
                <Stack alignItems="center">
                    <AvatarWithUpload imgAlt="Logo"
                                      imgSrc={logo}
                                      width={200}
                                      height="auto"
                                      variant="square"
                                      onUpdate={handleUpdateLogo}>
                        <Typography variant="h5">
                            Logo
                        </Typography>
                    </AvatarWithUpload>
                </Stack>
                <Typography variant="caption" textAlign="center">
                    (Il logo selezionato verrà applicato a tutti i modelli email)
                </Typography>
                <Divider/>

                <Stack spacing={2}>
                    <Stack alignItems="center">
                        <Typography variant="caption" color="text.primary">
                            Allineamento titolo
                        </Typography>
                        <ToggleButtonGroup name="titleAlignment"
                                           value={formik.values.titleAlignment}
                                           onChange={(event, newAlignment) => {
                                               formik.setFieldValue("titleAlignment", newAlignment);
                                           }}
                                           exclusive
                                           size="small"
                                           aria-label="titleAlignment">
                            <ToggleButton value="left" key="left" name="titleAlignment">
                                <FormatAlignLeftIcon />
                            </ToggleButton>
                            <ToggleButton value="center" key="center" name="titleAlignment">
                                <FormatAlignCenterIcon />
                            </ToggleButton>
                            <ToggleButton value="right" key="right" name="titleAlignment">
                                <FormatAlignRightIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    <TextField label="Colore primario"
                               value={formik.values.primaryColor}
                               name={"primaryColor"}
                               onChange={formik.handleChange}
                               autoFocus
                               error={formik.touched.primaryColor && Boolean(formik.errors.primaryColor)}
                               helperText={formik.touched.primaryColor && formik.errors.primaryColor}
                               type="color"/>

                    <TextField label="Colore sfondo principale"
                               value={formik.values.primaryBackgroundColor}
                               name={"primaryBackgroundColor"}
                               onChange={formik.handleChange}
                               autoFocus
                               error={formik.touched.primaryBackgroundColor && Boolean(formik.errors.primaryBackgroundColor)}
                               helperText={formik.touched.primaryBackgroundColor && formik.errors.primaryBackgroundColor}
                               type="color"/>

                    <TextField label="Colore sfondo secondario"
                               value={formik.values.secondaryBackgroundColor}
                               name={"secondaryBackgroundColor"}
                               onChange={formik.handleChange}
                               autoFocus
                               error={formik.touched.secondaryBackgroundColor && Boolean(formik.errors.secondaryBackgroundColor)}
                               helperText={formik.touched.secondaryBackgroundColor && formik.errors.secondaryBackgroundColor}
                               type="color"/>

                </Stack>
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button color="primary"
                    variant="text"
                    onClick={onClose}>
                Chiudi
            </Button>
            <Button onClick={formik.handleSubmit} variant="contained" autoFocus>
                Salva
            </Button>
        </DialogActions>
    </Dialog>
}

export default ThemeDialog;